/* =============================================================================
 * Formatacao para exibicao (pt-BR).
 *
 * Substitui LibGeral.toDisplay (~175 linhas de switch por TipoInformacao) pelo
 * Intl nativo. Aqui so' entra o que o Futigol realmente usa.
 * ========================================================================== */

import { paraData, paraNumero, vazio } from './valores.js';

const LOCALE = 'pt-BR';

const fmtDataCurta = new Intl.DateTimeFormat(LOCALE, { day: '2-digit', month: '2-digit', year: 'numeric' });
const fmtDataDiaMes = new Intl.DateTimeFormat(LOCALE, { day: '2-digit', month: '2-digit' });
const fmtDataExtensa = new Intl.DateTimeFormat(LOCALE, { day: '2-digit', month: 'long', year: 'numeric' });
const fmtHoraMinuto = new Intl.DateTimeFormat(LOCALE, { hour: '2-digit', minute: '2-digit' });
const fmtMesAno = new Intl.DateTimeFormat(LOCALE, { month: 'long', year: 'numeric' });
const fmtDiaSemana = new Intl.DateTimeFormat(LOCALE, { weekday: 'long' });
const fmtDiaSemanaCurto = new Intl.DateTimeFormat(LOCALE, { weekday: 'short' });
const fmtMoeda = new Intl.NumberFormat(LOCALE, { style: 'currency', currency: 'BRL' });

export function dataCurta(valor) {
    const d = paraData(valor);
    return d ? fmtDataCurta.format(d) : '';
}

/**
 * Faixa de datas em uma frase so': '01/05/2026 a 30/11/2026'.
 *
 * Com uma ponta so' o texto MUDA em vez de mostrar meia faixa - "01/05/2026 a"
 * mentiria sobre um fim que nao foi cadastrado, e so' a data solta nao diz se
 * ela e' o comeco ou o fim.
 */
export function periodo(inicio, fim) {
    const de = dataCurta(inicio);
    const ate = dataCurta(fim);

    if (de && ate) return de === ate ? de : `${de} a ${ate}`;
    if (de) return `A partir de ${de}`;
    if (ate) return `Até ${ate}`;
    return '';
}

export function dataDiaMes(valor) {
    const d = paraData(valor);
    return d ? fmtDataDiaMes.format(d) : '';
}

export function dataExtensa(valor) {
    const d = paraData(valor);
    return d ? fmtDataExtensa.format(d) : '';
}

/**
 * 'agosto de 2026' - o titulo de uma grade de mes.
 *
 * O Intl devolve o mes em minuscula em pt-BR; quem quiser inicial maiuscula
 * capitaliza na propria tela, porque no meio de frase a minuscula e' que esta'
 * certa.
 */
export function mesAno(valor) {
    const d = paraData(valor);
    return d ? fmtMesAno.format(d) : '';
}

export function horaMinuto(valor) {
    const d = paraData(valor);
    return d ? fmtHoraMinuto.format(d) : '';
}

export function dataHora(valor) {
    const d = paraData(valor);
    return d ? `${fmtDataCurta.format(d)} ${fmtHoraMinuto.format(d)}` : '';
}

export function diaSemana(valor) {
    const d = paraData(valor);
    return d ? fmtDiaSemana.format(d) : '';
}

export function diaSemanaCurto(valor) {
    const d = paraData(valor);
    if (!d) return '';
    return fmtDiaSemanaCurto.format(d).replace('.', '').toUpperCase();
}

/**
 * Diferenca em dias de CALENDARIO: 0 e' hoje, negativo e' passado.
 *
 * Compara o dia, nao o instante - duas datas do mesmo dia dao 0 mesmo com 23
 * horas de distancia entre elas, que e' o que uma tela de jogos precisa.
 *
 * @returns {number|null} null quando a data nao e' valida
 */
export function diasAte(valor, referencia = new Date()) {
    const d = paraData(valor);
    if (!d) return null;
    const dia = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
    return Math.round((dia(d) - dia(referencia)) / 86400000);
}

/** 'Hoje' / 'Amanha' / 'Ontem' quando aplicavel; senao a data curta. */
export function dataRelativa(valor, referencia = new Date()) {
    const d = paraData(valor);
    if (!d) return '';

    const diferenca = diasAte(d, referencia);

    if (diferenca === 0) return 'Hoje';
    if (diferenca === 1) return 'Amanhã';
    if (diferenca === -1) return 'Ontem';
    if (diferenca > 1 && diferenca < 7) return diaSemana(d);
    return dataCurta(d);
}

export function numero(valor, casas = 0) {
    if (vazio(valor)) return '';
    return new Intl.NumberFormat(LOCALE, { minimumFractionDigits: casas, maximumFractionDigits: casas }).format(paraNumero(valor));
}

/** O backend ja' devolve o percentual na escala 0-100. */
export function percentual(valor, casas = 1) {
    if (vazio(valor)) return '';
    return `${numero(valor, casas)}%`;
}

export function moeda(valor) {
    if (vazio(valor)) return '';
    return fmtMoeda.format(paraNumero(valor));
}

const CONECTIVOS = new Set(['de', 'da', 'do', 'dos', 'das', 'e']);

/** Iniciais para o fallback de avatar/escudo quando nao ha' imagem. */
export function iniciais(nome, quantidade = 2) {
    if (vazio(nome)) return '?';
    const partes = String(nome)
        .trim()
        .split(/\s+/)
        .filter((p) => !CONECTIVOS.has(p.toLowerCase()));
    const letras = partes.slice(0, quantidade).map((p) => p[0]);
    return (letras.join('') || String(nome).trim()[0]).toUpperCase();
}

/** '3 x 1' ou '-' quando a partida ainda nao tem placar. */
export function placar(golsMandante, golsVisitante) {
    if (vazio(golsMandante) && vazio(golsVisitante)) return '';
    return `${paraNumero(golsMandante)} × ${paraNumero(golsVisitante)}`;
}
